import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Toaster } from '../components/ui/sonner';
import { SignIn } from '../features/auth/sign-in/SignIn';
import SignUp from '../features/auth/sign-up/SignUp';
import { OverviewPage } from '../features/settings/overview';
import { SuccessHtmlPage } from '../features/pricing/page-payment-success';
import { FailedHtmlPage } from '../features/pricing/page-payment-failed';
import ErrorPopup from '../components/error-popup';
import ForgotPassword from '../features/auth/sign-in/ForgotPassword';
import { SidebarProvider } from '../components/ui/sidebar';
import { HtmlContent } from '../features/pricing/redirect-pages';
import { PricingPage } from '../features/pricing/pricing';
import { ApiPage } from '../features/api/ApiPage';
import { SupportPage } from '../features/support/SupportPage';
import { AppSideBar } from './sidebar';
import { MobileNav } from './mobile-nav';
import { ErrorProvider } from '../context/ErrorContext';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAtom } from 'jotai';
import { user } from '../state/atoms/user.atom';
import { User } from '../types/user.types';
import { makeApiRequest } from '../lib/http/makeApiRequest';
import Intercom from '@intercom/messenger-js-sdk';
import { ConfigProvider } from 'antd';
import { OB_ANTD_THEME } from './antd-theme';

const AppContent = () => {
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const [userAtom, setUserAtom] = useAtom(user);

  React.useEffect(() => {
    makeApiRequest('/v1/user', {
      method: 'GET',
    }).then((userData: User) => {
      console.log('userData:', userData);
      setUserAtom(userData);
    }).catch((err: unknown) => {
      console.error('Failed to fetch user data:', err);
      setError('Failed to load user data');
    });
  }, [setUserAtom]);

  React.useEffect(() => {
    const config: Parameters<typeof Intercom>[0] = {
      app_id: 'roi3alji',
    };
    if (userAtom) {
      config.user_id = userAtom.id || 'unknown';
      config.email = userAtom.email || 'unkonwn';
    }
    Intercom(config);
  }, [userAtom]);

  const handleErrorClose = () => {
    setError(null);
  };

  const isAuthPage = ['/signin', '/signup', '/password-recovery', '/', '/logout'].includes(location.pathname);

  if (isAuthPage) {

    return (
      <>
        {error && <ErrorPopup message={error} onClose={handleErrorClose} />}
        <Routes>
          <Route path='/' element={<SignIn />} />
          <Route path='/signup' element={<SignUp />} />
          <Route path='/signin' element={<SignIn />} />
          <Route path='/password-recovery' element={<ForgotPassword />} />
          <Route path='/logout' element={<SignIn />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      {error && <ErrorPopup message={error} onClose={handleErrorClose} />}
      <ConfigProvider theme={OB_ANTD_THEME}>
        <SidebarProvider className='bg-ob-shell' style={{ '--sidebar-width': '232px' } as React.CSSProperties}>
          <AppSideBar />
          <main className='flex min-w-0 flex-1 flex-col overflow-x-hidden bg-ob-canvas font-ob text-ob-text-secondary [color-scheme:dark] md:rounded-l-[32px]'>
            <MobileNav />
            <div className='min-w-0 flex-1'>
              <Routes>
                <Route path='/settings' element={<OverviewPage />} />
                <Route path='/checkout/success' element={<HtmlContent html={SuccessHtmlPage} />} />
                <Route path='/checkout/failed' element={<HtmlContent html={FailedHtmlPage} />} />
                <Route path='/pricing' element={<PricingPage />} />
                <Route path='/api' element={<ApiPage />} />
                <Route path='/support' element={<SupportPage />} />
              </Routes>
            </div>
          </main>
        </SidebarProvider>
      </ConfigProvider>
    </>
  );
}

export const App = () => {
  return (
    <ErrorBoundary>
      <ErrorProvider>
        <Router>
          <AppContent />
          <Toaster />
        </Router>
      </ErrorProvider>
    </ErrorBoundary>
  );
}
